import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useParams, useSearchParams } from 'react-router-dom'
import { Typography } from 'antd'
import {
  CommentDomainService,
  useCommentActions,
  useCommentWorkflow,
  useFetchComments
} from 'domain/comment'
import useBuildingUsersAndGroups from 'hooks/useBuildingUsersAndGroups'
import useDirectory from 'hooks/useDirectory'
import useMutation from 'hooks/useMutation'
import { CurrentUser, Role } from 'models/User'
import { useRecoilState, useRecoilValue } from 'recoil'
import Api from 'services/api'
import {
  buildingModeState,
  listPointState,
  SelectedMode,
  selectedPoint,
  selectedPointDetail,
  selectedSubImageState
} from 'store/buildingStore'
import { displayToggleState } from 'store/displayToggleStore'
import { currentAnnotation } from 'store/krpanoStore'
import currentViewerState from 'store/viewerStore'
import useSWR from 'swr'
import { AnnotationType, Point } from 'types/building'
import {
  getPointVersion,
  handlePointLockedError,
  handlePointVersionError
} from 'utils/pointVersion'

import Button from 'components/atoms/Button'
import ConfirmModal from 'components/atoms/Modal'
import Title from 'components/atoms/Title'

import ModalCommentDetailRN from './ModalCommentDetailRN'
import ModalCommentFormRN from './ModalCommentFormRN'

export default function AnnotationCommentModalRN() {
  const { t } = useTranslation()
  const location = useLocation()
  const { id, floorId: paramFloorId } = useParams()
  const [searchParams] = useSearchParams()
  const [currentViewer, setCurrentViewer] = useRecoilState(currentViewerState)
  const selectedPointValue = useRecoilValue(selectedPoint)
  const [currentSelected, setCurrentSelected] =
    useRecoilState(currentAnnotation)
  const [pointDetail, setPointDetail] = useRecoilState(selectedPointDetail)
  const displayToggle = useRecoilValue(displayToggleState)
  const [points, setPoints] = useRecoilState(listPointState)
  const buildingMode = useRecoilValue(buildingModeState)

  const isViewerScreen = useMemo(() => {
    return (
      location.pathname.includes('/viewer') ||
      location.pathname.includes('/tour')
    )
  }, [location.pathname])

  const { getBuiildingType } = useDirectory()
  const { data: building } = useSWR(
    id
      ? {
          url: `/v1/buildings/by-type?id=${id}&building_type=${getBuiildingType()}`
        }
      : null,
    {
      revalidateIfStale: false,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      dedupingInterval: 300000
    }
  )
  const isViewerAction = building?.action === 'VIEW'

  const activePointDetail = currentViewer?.pointDetail || pointDetail

  const setUnifiedPointDetail = useCallback(
    (updater: any) => {
      setPointDetail(updater)
      if (currentViewer?.pointDetail) {
        setCurrentViewer((prev) => {
          if (!prev?.pointDetail) return prev
          const updated =
            typeof updater === 'function' ? updater(prev.pointDetail) : updater
          return {
            ...prev,
            pointDetail: updated
          }
        })
      }
    },
    [setPointDetail, currentViewer?.pointDetail, setCurrentViewer]
  )

  const effectiveFloorId = useMemo(() => {
    if (isViewerScreen) {
      return (
        (currentViewer?.selectedFloor
          ? String(currentViewer.selectedFloor)
          : undefined) ||
        (activePointDetail?.floorId
          ? String(activePointDetail.floorId)
          : undefined) ||
        (currentSelected?.floor_id
          ? String(currentSelected.floor_id)
          : undefined) ||
        paramFloorId ||
        searchParams.get('floor') ||
        ''
      )
    }
    return (
      paramFloorId ||
      (activePointDetail?.floorId
        ? String(activePointDetail.floorId)
        : undefined) ||
      searchParams.get('floor') ||
      (currentViewer?.selectedFloor
        ? String(currentViewer.selectedFloor)
        : undefined) ||
      (currentSelected?.floor_id
        ? String(currentSelected.floor_id)
        : undefined) ||
      ''
    )
  }, [
    isViewerScreen,
    currentViewer?.selectedFloor,
    activePointDetail,
    currentSelected,
    paramFloorId,
    searchParams
  ])

  const currentSelectedPoint = useMemo(() => {
    if (isViewerScreen) {
      return (
        (currentViewer?.selectedPoint
          ? String(currentViewer.selectedPoint)
          : undefined) ||
        (activePointDetail?.id ? String(activePointDetail.id) : undefined) ||
        (currentSelected?.point_id
          ? String(currentSelected.point_id)
          : undefined) ||
        (selectedPointValue ? String(selectedPointValue) : undefined) ||
        searchParams.get('point') ||
        ''
      )
    }
    return (
      (activePointDetail?.id ? String(activePointDetail.id) : undefined) ||
      (selectedPointValue ? String(selectedPointValue) : undefined) ||
      (currentSelected?.point_id
        ? String(currentSelected.point_id)
        : undefined) ||
      (currentViewer?.selectedPoint
        ? String(currentViewer.selectedPoint)
        : undefined) ||
      searchParams.get('point') ||
      ''
    )
  }, [
    isViewerScreen,
    currentViewer?.selectedPoint,
    activePointDetail,
    currentSelected,
    selectedPointValue,
    searchParams
  ])

  const { data: profile } = useSWR<CurrentUser | null>('v1/users/me')
  const isLockedByMe = useMemo(() => {
    if (activePointDetail?.isLocked) {
      return Boolean(
        profile?.id && String(activePointDetail.lockedBy) === String(profile.id)
      )
    }
    if (displayToggle?.isLocked) {
      return Boolean(
        profile?.id && String(displayToggle.lockedBy) === String(profile.id)
      )
    }
    return false
  }, [
    activePointDetail?.isLocked,
    activePointDetail?.lockedBy,
    displayToggle?.isLocked,
    displayToggle?.lockedBy,
    profile?.id
  ])

  const isLockedByAnother = useMemo(() => {
    if (activePointDetail?.isLocked) {
      return Boolean(
        activePointDetail.lockedBy &&
          profile?.id &&
          String(activePointDetail.lockedBy) !== String(profile.id)
      )
    }
    if (displayToggle?.isLocked) {
      return Boolean(
        displayToggle.lockedBy &&
          profile?.id &&
          String(displayToggle.lockedBy) !== String(profile.id)
      )
    }
    return false
  }, [
    activePointDetail?.isLocked,
    activePointDetail?.lockedBy,
    displayToggle?.isLocked,
    displayToggle?.lockedBy,
    profile?.id
  ])

  const { userList, userGroups, getOrgString, getRoleString } =
    useBuildingUsersAndGroups(id)

  const { comments, mutate: mutateComments } = useFetchComments({
    buildingId: id!,
    floorId: effectiveFloorId,
    pointId: currentSelectedPoint
  })
  const { addReply, updateReply, deleteReply, deleteComment } =
    useCommentActions()
  const { saveCommentWithAnnotation } = useCommentWorkflow()
  const { trigger: deleteAnnotation } = useMutation('v1/annotations')

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false)
  const [pendingDeleteId, setPendingDeleteId] = useState<number | null>(null)

  useEffect(() => {
    setIsEditing(false)
  }, [currentSelected?.id])

  const domainService = useMemo(
    () =>
      new CommentDomainService({
        saveCommentWithAnnotation,
        addReply,
        deleteComment,
        deleteAnnotation,
        mutateComments,
        setCurrentSelected,
        setPointDetail: setUnifiedPointDetail,
        setPoints,
        userList,
        userGroups,
        currentUser: profile,
        t
      }),
    [
      saveCommentWithAnnotation,
      addReply,
      deleteComment,
      deleteAnnotation,
      mutateComments,
      setCurrentSelected,
      setUnifiedPointDetail,
      setPoints,
      userList,
      userGroups,
      profile,
      t
    ]
  )

  const commentDetail = useMemo(
    () =>
      CommentDomainService.normalizeCommentDetail(
        currentSelected,
        comments,
        id,
        effectiveFloorId,
        currentSelectedPoint,
        userList
      ),
    [
      currentSelected,
      comments,
      id,
      effectiveFloorId,
      currentSelectedPoint,
      userList
    ]
  )

  const commentAuthorId =
    commentDetail?.authorId ||
    (currentSelected as any)?.author_id ||
    (currentSelected as any)?.authorId ||
    (currentSelected as any)?.created_by ||
    (currentSelected as any)?.createdBy

  const isOwner = Boolean(
    profile?.id &&
      commentAuthorId &&
      String(profile.id) === String(commentAuthorId)
  )

  const isSA = profile?.role === Role.SA

  const canEditOrDelete = !isViewerScreen && isLockedByMe && (isOwner || isSA)

  const selectedSubImage = useRecoilValue(selectedSubImageState)

  const lastPreCheckRef = useRef<number>(0)
  const handlePreCheck = useCallback(async (): Promise<boolean> => {
    const now = Date.now()
    if (now - lastPreCheckRef.current < 2000) {
      return true
    }

    // 1. Client-side lock check
    if (isLockedByAnother) {
      handlePointLockedError(
        {
          response: { data: { error: { key: 'point_is_locked_by_another' } } }
        },
        t
      )
      return false
    }

    // 2. Server-side fresh point verification
    if (currentSelectedPoint && id && effectiveFloorId) {
      try {
        const freshPoint = await Api.get<Point>(
          `/v1/points/${currentSelectedPoint}`,
          {
            buildingId: id,
            floorId: effectiveFloorId
          }
        )

        if (
          freshPoint?.isLocked &&
          profile?.id &&
          freshPoint?.lockedBy &&
          String(freshPoint.lockedBy) !== String(profile.id)
        ) {
          handlePointLockedError(
            {
              response: {
                data: { error: { key: 'point_is_locked_by_another' } }
              }
            },
            t
          )
          return false
        }

        const expectedVersion = getPointVersion(
          currentSelectedPoint,
          activePointDetail,
          points
        )
        if (
          expectedVersion != null &&
          freshPoint?.pointVersionId != null &&
          freshPoint.pointVersionId !== expectedVersion
        ) {
          handlePointVersionError(
            {
              response: {
                data: {
                  error: {
                    key: 'point_version_mismatch',
                    current_version: freshPoint.pointVersionId,
                    sent_version: expectedVersion
                  }
                }
              }
            },
            t
          )
          return false
        }
      } catch (err) {
        if (handlePointLockedError(err, t)) return false
        if (handlePointVersionError(err, t)) return false
      }
    }

    lastPreCheckRef.current = Date.now()
    return true
  }, [
    isLockedByAnother,
    currentSelectedPoint,
    id,
    effectiveFloorId,
    profile?.id,
    activePointDetail,
    points,
    t
  ])

  const handleSave = async (payload: any) => {
    setIsSubmitting(true)
    try {
      const canProceed = await handlePreCheck()
      if (!canProceed) return

      await domainService.handleSave(
        currentSelected,
        currentSelectedPoint,
        id || 0,
        effectiveFloorId || 0,
        {
          ...payload,
          subImageId:
            payload?.subImageId ??
            payload?.sub_image_id ??
            selectedSubImage ??
            currentViewer?.selectedImage
        },
        activePointDetail,
        points
      )
      setIsEditing(false)
    } catch {
      // Error notifications are handled in comment workflow
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleDelete = (commentId: number) => {
    setPendingDeleteId(commentId)
    setIsDeleteConfirmOpen(true)
  }

  const handleConfirmDelete = async () => {
    if (!pendingDeleteId) return
    setIsDeleteConfirmOpen(false)
    setIsSubmitting(true)
    try {
      await domainService.handleDelete(
        pendingDeleteId,
        currentSelected,
        id,
        effectiveFloorId,
        currentSelectedPoint,
        activePointDetail,
        points
      )
    } finally {
      setIsSubmitting(false)
      setPendingDeleteId(null)
    }
  }

  const handleReply = async (commentId: number, payload: any) => {
    setIsSubmitting(true)
    try {
      await domainService.handleReply(commentId, payload)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleUpdateReply = async (replyId: number, payload: any) => {
    setIsSubmitting(true)
    try {
      await updateReply(replyId, payload)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleDeleteReply = async (replyId: number) => {
    setIsSubmitting(true)
    try {
      await deleteReply(replyId)
    } finally {
      setIsSubmitting(false)
    }
  }

  const isCommentAnnotation = useMemo(() => {
    if (!currentSelected) return false
    const typeVal = (currentSelected as any).type
    return (
      Number(typeVal) === Number(AnnotationType.COMMENT) ||
      typeVal === 'COMMENT' ||
      typeVal === '42' ||
      Boolean((currentSelected as any).comment_object)
    )
  }, [currentSelected])

  const isCommentToolbarSelected =
    buildingMode.selectedMode === SelectedMode.RN_COMMENT_ANNOTATION

  const isOpen = useMemo(() => {
    if (!isCommentAnnotation) return false
    if (commentDetail) return true
    if (isViewerAction || isViewerScreen) return false
    return isLockedByMe && isCommentToolbarSelected
  }, [
    isViewerAction,
    isViewerScreen,
    isCommentAnnotation,
    commentDetail,
    isLockedByMe,
    isCommentToolbarSelected
  ])

  return (
    <>
      {isOpen && (!commentDetail || isEditing) && (
        <ModalCommentFormRN
          open={isOpen}
          comment={commentDetail}
          userList={userList}
          userGroups={userGroups}
          getOrgString={getOrgString}
          getRoleString={getRoleString}
          onCancel={() => {
            if (commentDetail) {
              setIsEditing(false)
            } else {
              domainService.handleCancel(currentSelected)
            }
          }}
          onSave={handleSave}
          onPreCheck={handlePreCheck}
          onDelete={handleDelete}
          isSubmitting={isSubmitting}
        />
      )}

      {isOpen && commentDetail && !isEditing && (
        <ModalCommentDetailRN
          open={isOpen}
          comment={commentDetail}
          userList={userList}
          userGroups={userGroups}
          getOrgString={getOrgString}
          getRoleString={getRoleString}
          onCancel={() => domainService.handleCancel(currentSelected)}
          onSave={handleSave}
          onReply={handleReply}
          onUpdateReply={handleUpdateReply}
          onDeleteReply={handleDeleteReply}
          onEdit={canEditOrDelete ? () => setIsEditing(true) : undefined}
          onDelete={canEditOrDelete ? handleDelete : undefined}
          onRefresh={async () => {
            await mutateComments()
          }}
          isSubmitting={isSubmitting}
        />
      )}

      {isDeleteConfirmOpen && (
        <ConfirmModal
          open={isDeleteConfirmOpen}
          onCancel={() => {
            setIsDeleteConfirmOpen(false)
            setPendingDeleteId(null)
          }}
          width={440}
          isEnterable
          onEnter={handleConfirmDelete}
        >
          <Title
            title={t('common.titleDeleteAnnotation')}
            bottomMargin="mb-8"
          />

          <div className="flex flex-col gap-10 text-center">
            <Typography className="text-[14px] leading-[21px]">
              {t('comment.deleteConfirmation', 'このコメントを削除しますか？')}
            </Typography>
            <div className="flex justify-center gap-10">
              <Button
                sizing="w-[140px] h-[50px]"
                onClick={() => {
                  setIsDeleteConfirmOpen(false)
                  setPendingDeleteId(null)
                }}
              >
                {t('building.buttonCancel')}
              </Button>

              <Button
                sizing="w-[140px] h-[50px]"
                className="bg-[#B80808]"
                onClick={handleConfirmDelete}
              >
                {t('common.delete')}
              </Button>
            </div>
          </div>
        </ConfirmModal>
      )}
    </>
  )
}
